import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Users, 
  Globe, 
  AlertTriangle,
  TrendingUp,
  Activity,
  RefreshCw
} from 'lucide-react';
import dashboardService, { DashboardStats as DashboardStatsType } from '../../services/dashboard';

interface DashboardStatsProps {
  currentSite?: any;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ currentSite }) => {
  const [stats, setStats] = useState<DashboardStatsType>({
    totalTenants: 0,
    totalSites: 0,
    activeUsers: 0,
    blockedSites: 0,
    totalWeighings: 0,
    pendingWeighings: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, [currentSite]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardStats = await dashboardService.getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStats();
  };

  if (loading) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Statistiques du Dashboard</h2>
          <button
            onClick={handleRefresh}
            disabled
            className="p-2 text-gray-400 cursor-not-allowed"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
              <div className="flex items-center">
                <div className="p-2 bg-gray-200 rounded-lg w-12 h-12"></div>
                <div className="ml-4 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Statistiques du Dashboard</h2>
          <button
            onClick={handleRefresh}
            className="p-2 text-orange-600 hover:text-orange-700 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Tenants',
      value: stats.totalTenants,
      icon: <Globe className="w-6 h-6 text-blue-600" />,
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      description: 'Organisations actives'
    },
    {
      title: 'Total Sites',
      value: stats.totalSites,
      icon: <Building2 className="w-6 h-6 text-green-600" />,
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
      description: 'Sites opérationnels'
    },
    {
      title: 'Utilisateurs Actifs',
      value: stats.activeUsers,
      icon: <Users className="w-6 h-6 text-purple-600" />,
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600',
      description: 'Utilisateurs connectés'
    },
    {
      title: 'Sites Bloqués',
      value: stats.blockedSites,
      icon: <AlertTriangle className="w-6 h-6 text-orange-600" />,
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-600',
      description: 'Sites suspendus'
    }
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Statistiques du Dashboard</h2>
        <button
          onClick={handleRefresh}
          className="p-2 text-gray-600 hover:text-gray-700 transition-colors"
          title="Rafraîchir les statistiques"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className={`p-2 ${card.bgColor} rounded-lg`}>
                {card.icon}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-xs text-gray-500">{card.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Statistiques supplémentaires pour le site actuel */}
      {currentSite && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Pesées (Site actuel)</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalWeighings}</p>
                <p className="text-xs text-gray-500">Site: {currentSite.name}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Activity className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pesées en Attente</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingWeighings}</p>
                <p className="text-xs text-gray-500">En cours de traitement</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardStats;
